import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { X, Clock, RotateCcw, GitCompare, FileText, Loader2, Lock } from 'lucide-react';

const API = `${window.__API_BASE}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const REASON_LABEL = {
  initial: 'Initial',
  manual_edit: 'Manual edit',
  napkin_approved: 'Napkin approved',
  sketch_reanchor: 'Sketch re-anchor',
  blueprint_approved: 'Blueprint approved',
  keyframe_feedback: 'Keyframe feedback',
  dop_spec_changed: 'DOP edit',
  llg_restamp: 'Look re-stamp',
  director_prompt_regen: 'Regenerated',
};
const reasonLabel = (r) => REASON_LABEL[r] || (r?.startsWith('rollback_to_') ? `Restored v${r.split('_').pop()}` : r);
const when = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

export const DirectorPromptHistory = ({ open, onClose, shotId, shotLabel, onRestored }) => {
  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [locked, setLocked] = useState(false);
  const [selected, setSelected] = useState(null);   // version_id being viewed
  const [detail, setDetail] = useState(null);        // {prompt,...}
  const [diff, setDiff] = useState(null);            // string | null
  const [mode, setMode] = useState('text');          // 'text' | 'diff'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const loadList = useCallback(async () => {
    if (!shotId) return;
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/shots/${shotId}/director-prompt/versions`, { headers: authHeader() });
      setVersions(r.data.versions || []);
      setActiveId(r.data.active_version_id);
      setLocked(!!r.data.locked);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load history');
    } finally { setLoading(false); }
  }, [shotId]);

  useEffect(() => { if (open) { loadList(); setSelected(null); setDetail(null); setDiff(null); setMode('text'); } }, [open, loadList]);

  const view = async (vid) => {
    setSelected(vid); setMode('text'); setDiff(null); setDetail(null);
    try {
      const r = await axios.get(`${API}/shots/${shotId}/director-prompt/versions/${vid}`, { headers: authHeader() });
      setDetail(r.data);
    } catch (e) { setError('Failed to load version'); }
  };

  const showDiff = async (vid) => {
    setMode('diff');
    try {
      const r = await axios.get(`${API}/shots/${shotId}/director-prompt/versions/${vid}/diff`, { headers: authHeader() });
      setDiff(r.data.diff || '(no differences vs current)');
    } catch (e) { setDiff('Failed to load diff'); }
  };

  const restore = async (vid, seq) => {
    if (busy) return;
    const unlock = locked ? window.confirm('This shot is LOCKED. Restore anyway? This is audit-logged.') : false;
    if (locked && !unlock) return;
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/shots/${shotId}/director-prompt/restore`,
        { version_id: vid, unlock }, { headers: authHeader() });
      await loadList();
      setSelected(null); setDetail(null); setDiff(null);
      onRestored?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Restore failed');
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
         style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)' }}
         data-testid="director-prompt-history-modal"
         onClick={onClose}>
      <div className="w-full max-w-4xl max-h-[85vh] rounded-2xl border border-ai-border overflow-hidden flex flex-col"
           style={{ background: 'rgba(11,15,25,0.98)' }}
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-ai-border">
          <div className="flex items-center gap-2 text-white/90">
            <Clock className="w-4 h-4 text-ai-accent" />
            <span className="text-sm font-semibold tracking-wide">Director's Prompt — History</span>
            <span className="text-[11px] text-white/40">{shotLabel}</span>
            {locked && <span className="text-[10px] text-amber-400/90 inline-flex items-center gap-1"><Lock className="w-3 h-3" />locked</span>}
          </div>
          <button onClick={onClose} data-testid="director-prompt-history-close"
                  className="p-1.5 text-white/50 hover:text-white rounded-lg hover:bg-white/5 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {error && <div className="px-4 py-2 text-[11px] text-red-400 bg-red-500/10 border-b border-red-500/20">{error}</div>}

        <div className="flex-1 flex min-h-0">
          {/* Version list */}
          <div className="w-64 border-r border-ai-border overflow-y-auto shrink-0" data-testid="director-prompt-version-list">
            {loading ? (
              <div className="flex items-center gap-2 text-[11px] text-white/50 p-4">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />Loading…
              </div>
            ) : versions.length === 0 ? (
              <div className="text-[11px] text-white/40 p-4">No versions yet.</div>
            ) : (
              [...versions].reverse().map((v) => {
                const isActive = v.version_id === activeId;
                const isSel = v.version_id === selected;
                return (
                  <button key={v.version_id}
                    onClick={() => view(v.version_id)}
                    data-testid={`director-prompt-version-row-${v.seq}`}
                    className={`w-full text-left px-3 py-2 border-b border-white/5 transition-colors ${isSel ? 'bg-ai-accent/15' : 'hover:bg-white/5'}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-semibold text-white/85">v{v.seq} · {reasonLabel(v.reason)}</span>
                      {isActive && <span className="text-[9px] text-emerald-400/90">current</span>}
                    </div>
                    <div className="text-[9px] text-white/40 mt-0.5">{v.triggered_by}</div>
                    <div className="text-[9px] text-white/30">{when(v.created_at)}</div>
                  </button>
                );
              })
            )}
          </div>

          {/* Detail pane */}
          <div className="flex-1 flex flex-col min-w-0">
            {!selected ? (
              <div className="flex-1 flex items-center justify-center text-[11px] text-white/40">
                Select a version to view its prompt or diff it against the current.
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 px-3 py-2 border-b border-ai-border">
                  <button onClick={() => { setMode('text'); }} data-testid="director-prompt-view-text"
                    className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded transition-colors ${mode === 'text' ? 'bg-ai-accent/20 text-ai-accent' : 'text-white/50 hover:text-white'}`}>
                    <FileText className="w-3 h-3" />Full text
                  </button>
                  <button onClick={() => showDiff(selected)} data-testid="director-prompt-view-diff"
                    className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded transition-colors ${mode === 'diff' ? 'bg-ai-accent/20 text-ai-accent' : 'text-white/50 hover:text-white'}`}>
                    <GitCompare className="w-3 h-3" />Diff vs current
                  </button>
                  <div className="flex-1" />
                  {selected !== activeId && (
                    <button onClick={() => restore(selected, detail?.seq)} disabled={busy}
                      data-testid="director-prompt-restore-btn"
                      className="inline-flex items-center gap-1 text-[10px] px-2.5 py-1 rounded bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 disabled:opacity-40 transition-colors">
                      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}Restore this version
                    </button>
                  )}
                </div>
                <div className="flex-1 overflow-y-auto p-3">
                  {mode === 'diff' ? (
                    <pre className="text-[10px] font-mono whitespace-pre-wrap break-words leading-relaxed" data-testid="director-prompt-diff-text">
                      {(diff || '').split('\n').map((ln, i) => (
                        <div key={i} className={ln.startsWith('+') && !ln.startsWith('+++') ? 'text-emerald-400/90'
                          : ln.startsWith('-') && !ln.startsWith('---') ? 'text-red-400/80'
                          : ln.startsWith('@@') ? 'text-ai-accent/80' : 'text-white/60'}>{ln || '\u00A0'}</div>
                      ))}
                    </pre>
                  ) : (
                    <pre className="text-[10px] text-white/80 font-mono whitespace-pre-wrap break-words leading-relaxed"
                         data-testid="director-prompt-version-text">{detail ? detail.prompt : 'Loading…'}</pre>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DirectorPromptHistory;
